import styled from "@emotion/styled";
import RcSlider from "rc-slider";
import React from "react";

import { theme } from "@themes/themes";

import "rc-slider/assets/index.css";

interface SliderMark {
  value: number;
  label?: string | React.ReactNode;
}

interface CustomSliderProps {
  value: number;
  onChange: (value: number | number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  marks?: SliderMark[];
  leftLabel?: React.ReactNode;
  rightLabel?: React.ReactNode;
  disabled?: boolean;
  hideLabels?: boolean;
  valueFormatter?: (value: number) => React.ReactNode;
  "data-testid"?: string;
}

export const Slider = React.memo(
  ({
    value,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    marks,
    leftLabel,
    rightLabel,
    disabled,
    "data-testid": dataTestId,
    hideLabels = false,
    valueFormatter = (v: number) => v,
  }: CustomSliderProps) => {
    const rcMarks = marks?.reduce(
      (acc, mark) => {
        acc[mark.value] = mark.label;
        return acc;
      },
      {} as Record<number, React.ReactNode>,
    );

    const renderHandle = (props: React.HTMLAttributes<HTMLDivElement> & { style?: React.CSSProperties }) => {
      const progress = (value - min) / (max - min);
      const translate = -100 * progress;
      const transform = `translate(${translate}%, -50%)`;

      return (
        <Thumb data-testid={`${dataTestId}:handle`} {...props} style={{ ...props.style, transform }}>
          {valueFormatter(value)}
        </Thumb>
      );
    };

    return (
      <RootContainer data-testid={dataTestId} disabled={disabled}>
        {(leftLabel || rightLabel) && (
          <LabelsRow>
            {leftLabel && <LeftLabel>{leftLabel}</LeftLabel>}
            {rightLabel && <RightLabel>{rightLabel}</RightLabel>}
          </LabelsRow>
        )}
        <SliderRow>
          <StyledRcSlider
            disabled={disabled}
            handleRender={({ props }) => renderHandle(props)}
            hideLabels={hideLabels}
            marks={rcMarks}
            max={max}
            min={min}
            step={step}
            value={value}
            included
            onChange={onChange}
          />
        </SliderRow>
      </RootContainer>
    );
  },
);

const RootContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
`;

const LabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const LeftLabel = styled.div`
  ${theme.font.body12};
  color: ${theme.color("accent-contrast")};
`;

const RightLabel = styled.div`
  ${theme.font.body12};
  color: ${theme.color("accent-contrast")};
`;

const Thumb = styled.div`
  ${theme.font.body14};
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: ${theme.color("accent-contrast")};

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${theme.color("gray-4")};

  padding: 8px 14px;
  min-width: 40px;
  min-height: 32px;
  width: fit-content;

  border: 1px solid ${theme.color("gray-7")};
  border-radius: 8px;

  cursor: pointer;
  outline: none;

  z-index: 1;
  margin-top: 2px;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRcSlider = styled(RcSlider)<{ hideLabels: boolean }>`
  width: 100%;

  .rc-slider-rail {
    background: ${theme.color("gray-5")};
    height: 4px;
  }
  .rc-slider-track {
    background: ${theme.color("accent-9")};
    height: 4px;
  }
  .rc-slider-handle {
    opacity: 1;
    border: 1px solid ${theme.color("gray-7")};

    &:active {
      border: 1px solid ${theme.color("gray-7")};
      box-shadow: unset !important;
    }
  }
  .rc-slider-handle-dragging {
    box-shadow: unset !important;
  }

  .rc-slider-mark-text,
  .rc-slider-mark-text-active {
    top: 4px;
    ${theme.font.support10};
    color: ${theme.color("gray-11")} !important;

    ${({ hideLabels }) => hideLabels && "display: none;"}

    &:last-of-type {
      transform: translateX(-100%) !important;
    }
    &:first-of-type {
      transform: translateX(0%) !important;
    }
  }

  .rc-slider-dot,
  .rc-slider-dot-active {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: ${theme.color("gray-5")};
    border: none;
    top: 50%;
    margin-top: -4px;
    transform: translateX(-50%) rotate(45deg) !important;

    &:last-of-type {
      transform: translateX(-100%) rotate(45deg) !important;
    }
    &:first-of-type {
      transform: translateX(0%) rotate(45deg) !important;
    }
  }
  .rc-slider-dot-active {
    background: ${theme.color("accent-7")};
    border: 1px solid ${theme.color("accent-9")};
  }

  &.rc-slider-disabled {
    background: transparent !important;

    .rc-slider-handle {
      border: 1px solid ${theme.color("gray-7")} !important;
      background: ${theme.color("gray-4")} !important;
    }
  }
`;
